import React, { useState, useRef } from 'react';
import {
  Upload, FileText, CheckCircle, XCircle, AlertTriangle, ArrowLeft,
  RefreshCw, Info, ChevronDown, ChevronUp, Check, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useAnalyserMiseAJourMutation,
  useAppliquerMiseAJourMutation,
  type AnalyseMajResponse,
  type ChangementClasse,
  type NouvelEtudiant,
  type AppliquerMajResponse,
} from '../../services/etudiantsApi';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';

type Step = 'upload' | 'analyse' | 'confirmation' | 'resultat';

export const MiseAJourExcel: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [analyse, setAnalyse] = useState<AnalyseMajResponse | null>(null);
  const [resultat, setResultat] = useState<AppliquerMajResponse | null>(null);
  const [erreur, setErreur] = useState('');

  // Selections utilisateur
  const [changementsAcceptes, setChangementsAcceptes] = useState<Set<number>>(new Set());
  const [nouveauxAcceptes, setNouveauxAcceptes] = useState<Set<number>>(new Set());
  const [showChangements, setShowChangements] = useState(true);
  const [showNouveaux, setShowNouveaux] = useState(true);
  const [showInconnus, setShowInconnus] = useState(true);

  const [analyserMaj, { isLoading: analyseLoading }] = useAnalyserMiseAJourMutation();
  const [appliquerMaj, { isLoading: appliquerLoading }] = useAppliquerMiseAJourMutation();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setAnalyse(null);
      setResultat(null);
      setErreur('');
      setStep('upload');

      // Read file immediately to avoid ERR_UPLOAD_FILE_CHANGED
      const reader = new FileReader();
      reader.onload = () => {
        setFileBuffer(reader.result as ArrayBuffer);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleAnalyser = async () => {
    if (!fileBuffer) return;
    setErreur('');
    const formData = new FormData();
    const blob = new Blob([fileBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    formData.append('file', blob, fileName);

    try {
      const result = await analyserMaj(formData).unwrap();
      setAnalyse(result);
      // Pre-selectionner tous les changements et nouveaux
      setChangementsAcceptes(new Set(result.changements_classe.map((_, i) => i)));
      setNouveauxAcceptes(new Set(result.nouveaux.map((_, i) => i)));
      setStep('confirmation');
    } catch (e: any) {
      setErreur(e?.data?.error || "Erreur lors de l'analyse du fichier.");
    }
  };

  const toggleChangement = (idx: number) => {
    setChangementsAcceptes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleNouvel = (idx: number) => {
    setNouveauxAcceptes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAllChangements = () => {
    if (!analyse) return;
    if (changementsAcceptes.size === analyse.changements_classe.length) {
      setChangementsAcceptes(new Set());
    } else {
      setChangementsAcceptes(new Set(analyse.changements_classe.map((_, i) => i)));
    }
  };

  const toggleAllNouveaux = () => {
    if (!analyse) return;
    if (nouveauxAcceptes.size === analyse.nouveaux.length) {
      setNouveauxAcceptes(new Set());
    } else {
      setNouveauxAcceptes(new Set(analyse.nouveaux.map((_, i) => i)));
    }
  };

  const handleAppliquer = async () => {
    if (!analyse) return;
    setErreur('');

    const changements = analyse.changements_classe.filter((_, i) => changementsAcceptes.has(i));
    const nouveaux = analyse.nouveaux.filter((_, i) => nouveauxAcceptes.has(i));

    if (changements.length === 0 && nouveaux.length === 0) {
      setErreur('Aucune modification selectionnee.');
      return;
    }

    try {
      const result = await appliquerMaj({
        changements_confirmes: changements,
        nouveaux,
      }).unwrap();
      setResultat(result);
      setStep('resultat');
    } catch (e: any) {
      setErreur(e?.data?.error || "Erreur lors de l'application.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title="Mise à jour des étudiants"
        description="Importez un fichier Excel pour mettre à jour les classes et ajouter de nouveaux étudiants"
        back={
          <button onClick={() => navigate(-1)}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label="Retour">
            <ArrowLeft size={18} />
          </button>
        }
      />

      {/* Steps indicator */}
      <div className="flex items-center gap-2">
        {[
          { id: 'upload', label: '1. Fichier' },
          { id: 'confirmation', label: '2. Verification' },
          { id: 'resultat', label: '3. Resultat' },
        ].map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600" />}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              step === s.id || (step === 'analyse' && s.id === 'upload')
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : step === 'resultat' && s.id !== 'resultat'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}>
              {step === 'resultat' && s.id !== 'resultat' ? <CheckCircle size={14} /> : null}
              {s.label}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Erreur (unifié) */}
      {erreur && (
        <AlertBanner type="error" message={erreur} onClose={() => setErreur('')} />
      )}

      {/* ══════ ETAPE 1 : UPLOAD ══════ */}
      {(step === 'upload' || step === 'analyse') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Upload size={16} /> Selectionner le fichier Excel de mise a jour
          </h2>

          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              <strong>Colonnes attendues :</strong> Matricule, Nom, Prenom(s), Classe, Sexe, Date de naissance, Lieu de naissance.
              <br />Les noms existants ne seront pas modifies. Seules les classes seront mises a jour apres votre confirmation.
            </div>
          </div>

          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText size={24} className="text-green-500" />
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</div>
                  <div className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} Ko</div>
                </div>
              </div>
            ) : (
              <div className="text-gray-400">
                <Upload size={32} className="mx-auto mb-2" />
                <p className="text-sm">Cliquez pour selectionner un fichier Excel (.xlsx)</p>
              </div>
            )}
          </div>

          <button
            onClick={handleAnalyser}
            disabled={!fileBuffer || analyseLoading}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            <RefreshCw size={16} className={analyseLoading ? 'animate-spin' : ''} />
            {analyseLoading ? 'Analyse en cours...' : 'Analyser le fichier'}
          </button>
        </div>
      )}

      {/* ══════ ETAPE 2 : CONFIRMATION ══════ */}
      {step === 'confirmation' && analyse && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Lignes lues', value: analyse.stats.total_lignes, color: 'text-gray-900 dark:text-gray-100' },
              { label: 'Inchanges', value: analyse.stats.inchanges, color: 'text-green-600' },
              { label: 'Chg. classe', value: analyse.stats.changements_classe, color: 'text-amber-600' },
              { label: 'Nouveaux', value: analyse.stats.nouveaux, color: 'text-blue-600' },
              { label: 'Inconnus', value: analyse.stats.classes_inconnues, color: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Alertes */}
          {analyse.alertes.map((a, i) => (
            <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
              a.type === 'danger' ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-700 dark:text-red-300'
              : a.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300'
              : a.type === 'info' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300'
              : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
            }`}>
              {a.type === 'danger' ? <XCircle size={16} className="shrink-0 mt-0.5" />
               : a.type === 'warning' ? <AlertTriangle size={16} className="shrink-0 mt-0.5" />
               : a.type === 'info' ? <Info size={16} className="shrink-0 mt-0.5" />
               : <CheckCircle size={16} className="shrink-0 mt-0.5" />}
              {a.message}
            </div>
          ))}

          {/* ── Changements de classe ── */}
          {analyse.changements_classe.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setShowChangements(!showChangements)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <span className="text-sm font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Changements de classe ({analyse.changements_classe.length})
                  <span className="text-xs font-normal text-gray-500">
                    — {changementsAcceptes.size} selectionne(s)
                  </span>
                </span>
                {showChangements ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showChangements && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-750 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={changementsAcceptes.size === analyse.changements_classe.length}
                        onChange={toggleAllChangements}
                        className="rounded"
                      />
                      Tout selectionner
                    </label>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2">Matricule</th>
                          <th className="text-left px-3 py-2">Nom</th>
                          <th className="text-left px-3 py-2">Prenoms</th>
                          <th className="text-left px-3 py-2">Ancienne classe</th>
                          <th className="text-center px-2 py-2"></th>
                          <th className="text-left px-3 py-2">Nouvelle classe</th>
                          <th className="text-left px-3 py-2">Nouveau pole</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {analyse.changements_classe.map((ch, i) => (
                          <tr
                            key={i}
                            className={`cursor-pointer ${changementsAcceptes.has(i) ? 'bg-amber-50/50 dark:bg-amber-900/10' : 'opacity-50'}`}
                            onClick={() => toggleChangement(i)}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={changementsAcceptes.has(i)}
                                onChange={() => toggleChangement(i)}
                                className="rounded"
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{ch.matricule}</td>
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{ch.nom}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{ch.prenoms}</td>
                            <td className="px-3 py-2">
                              <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                                {ch.ancienne_classe}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-center text-gray-400">→</td>
                            <td className="px-3 py-2">
                              <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                                {ch.nouvelle_classe}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-500">{ch.nouveau_pole}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Nouveaux etudiants ── */}
          {analyse.nouveaux.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                onClick={() => setShowNouveaux(!showNouveaux)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-2">
                  <Info size={16} />
                  Nouveaux etudiants ({analyse.nouveaux.length})
                  <span className="text-xs font-normal text-gray-500">
                    — {nouveauxAcceptes.size} selectionne(s)
                  </span>
                </span>
                {showNouveaux ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showNouveaux && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-750 flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nouveauxAcceptes.size === analyse.nouveaux.length}
                        onChange={toggleAllNouveaux}
                        className="rounded"
                      />
                      Tout selectionner
                    </label>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2">Matricule</th>
                          <th className="text-left px-3 py-2">Nom</th>
                          <th className="text-left px-3 py-2">Prenoms</th>
                          <th className="text-left px-3 py-2">Classe</th>
                          <th className="text-left px-3 py-2">Pole</th>
                          <th className="text-left px-3 py-2">Option</th>
                          <th className="text-left px-3 py-2">Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {analyse.nouveaux.map((n, i) => (
                          <tr
                            key={i}
                            className={`cursor-pointer ${nouveauxAcceptes.has(i) ? '' : 'opacity-50'}`}
                            onClick={() => toggleNouvel(i)}
                          >
                            <td className="px-3 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={nouveauxAcceptes.has(i)}
                                onChange={() => toggleNouvel(i)}
                                className="rounded"
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{n.matricule}</td>
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{n.nom}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{n.prenoms}</td>
                            <td className="px-3 py-2">
                              <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">{n.classe}</span>
                            </td>
                            <td className="px-3 py-2 text-gray-500">{n.pole}</td>
                            <td className="px-3 py-2 text-gray-500 max-w-32 truncate">{n.option}</td>
                            <td className="px-3 py-2 text-gray-500">{n.type_formation}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Classes inconnues ── */}
          {analyse.classes_inconnues.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 overflow-hidden">
              <button
                onClick={() => setShowInconnus(!showInconnus)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <span className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
                  <XCircle size={16} />
                  Classes inconnues ({analyse.classes_inconnues.length})
                  <span className="text-xs font-normal text-gray-500">
                    — ces etudiants ne seront pas traites
                  </span>
                </span>
                {showInconnus ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showInconnus && (
                <div className="border-t border-red-200 dark:border-red-800 p-4 space-y-3">
                  {analyse.classes_inconnues.map((ci, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-red-50/50 dark:bg-red-900/10 rounded-lg">
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-mono font-bold">
                        {ci.classe}
                      </span>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">{ci.count} etudiant(s)</span>
                        {ci.exemples.length > 0 && (
                          <span className="ml-2 text-gray-400">
                            ex: {ci.exemples.map(e => `${e.nom} ${e.prenoms}`).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Ces classes n'existent pas dans la base. Creez-les d'abord dans la gestion des options/classes, puis relancez la mise a jour.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Boutons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => { setStep('upload'); setAnalyse(null); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-650 text-sm font-medium"
            >
              <ArrowLeft size={16} /> Retour
            </button>
            <button
              onClick={handleAppliquer}
              disabled={appliquerLoading || (changementsAcceptes.size === 0 && nouveauxAcceptes.size === 0)}
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
            >
              <Check size={16} />
              {appliquerLoading ? 'Application en cours...' : `Appliquer (${changementsAcceptes.size} modifications, ${nouveauxAcceptes.size} ajouts)`}
            </button>
          </div>
        </div>
      )}

      {/* ══════ ETAPE 3 : RESULTAT ══════ */}
      {step === 'resultat' && resultat && (
        <div className="space-y-4">
          <div className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${
            resultat.success
              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300'
          }`}>
            {resultat.success ? <CheckCircle size={20} className="shrink-0" /> : <AlertTriangle size={20} className="shrink-0" />}
            <div>
              <div className="font-semibold">{resultat.message}</div>
              <div className="mt-1 text-xs opacity-80">
                {resultat.nb_mis_a_jour} classe(s) modifiee(s) | {resultat.nb_crees} etudiant(s) cree(s) | {resultat.nb_erreurs} erreur(s)
              </div>
            </div>
          </div>

          {/* Details */}
          {resultat.details.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                Details des operations
              </div>
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {resultat.details.map((d, i) => (
                      <tr key={i} className={d.type === 'erreur' ? 'bg-red-50/50 dark:bg-red-900/5' : ''}>
                        <td className="px-3 py-2 w-20">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            d.type === 'maj' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                            : d.type === 'creation' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}>
                            {d.type === 'maj' ? 'Modifie' : d.type === 'creation' ? 'Cree' : 'Erreur'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{d.matricule || '-'}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{d.nom || d.message || '-'}</td>
                        <td className="px-3 py-2 text-gray-500">
                          {d.ancien && d.nouveau ? (
                            <span>{d.ancien} → {d.nouveau}</span>
                          ) : d.classe ? (
                            <span>{d.classe}</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions finales */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setStep('upload'); setSelectedFile(null); setFileBuffer(null); setFileName(''); setAnalyse(null); setResultat(null); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <RefreshCw size={16} /> Nouvelle mise a jour
            </button>
            <button
              onClick={() => navigate('/etudiants')}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
            >
              Voir les etudiants
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiseAJourExcel;
